import {
  UniversalNodeRegistry,
  isAddableSchema,
  type UniversalNodeSchema,
} from '@/features/backtest/schemas/universal-node-schema'
import { NODE_CATEGORY_CONFIG } from '@/features/backtest/schemas/node-category-config'
import { validateNodeConnection } from './universal-node-validation'

type NodeCategory = UniversalNodeSchema['category']

export interface ConnectableCategory {
  category: NodeCategory
  /** The palette's name for it ("Data Sources", "Indicators"). */
  label: string
  /** The palette's accent for it, hex. */
  color: string
  /** Every node in it that can feed the input, by name, sorted. */
  names: string[]
}

/** One output of a node the palette can add. */
type NodeOutput = UniversalNodeSchema['outputs'][number]

/**
 * The palette categories whose addable nodes have an output `feeds` lets
 * through, each with those nodes' names, sorted as the palette is.
 * connectableCategories asks it of one input; a subnet input asks it of every
 * node the input feeds (subnet-input-needs.ts).
 */
export function connectableCategoriesWhere(
  feeds: (schema: UniversalNodeSchema, output: NodeOutput) => boolean,
): ConnectableCategory[] {
  const byCategory = new Map<NodeCategory, Set<string>>()
  for (const schema of UniversalNodeRegistry.getAllSchemas()) {
    if (!isAddableSchema(schema)) continue
    if (!schema.outputs.some((output) => feeds(schema, output))) continue
    const names = byCategory.get(schema.category) ?? new Set<string>()
    names.add(schema.displayName)
    byCategory.set(schema.category, names)
  }
  return [...byCategory.entries()]
    .map(([category, names]) => ({
      category,
      label: NODE_CATEGORY_CONFIG[category]?.label ?? category,
      color: NODE_CATEGORY_CONFIG[category]?.color ?? '#6b7280',
      names: [...names].sort((a, b) => a.localeCompare(b)),
      order: NODE_CATEGORY_CONFIG[category]?.order ?? 99,
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...category }) => category)
}

/**
 * What can plug into one input, high level: the palette categories whose
 * nodes can feed it, each with those nodes' names (Chris, 2026-10-06: a "?"
 * by "Connect a source" that says it takes data, indicators, math...). Asks
 * the canvas's own wire rule (validateNodeConnection) of every node type the
 * palette can add, over each of its outputs, so it never offers a wire the
 * canvas would refuse. `targetData` is the node's own data: a few inputs take
 * different wires by setting.
 */
export function connectableCategories(
  targetType: string,
  inputId: string,
  targetData?: { parameters?: unknown },
): ConnectableCategory[] {
  return connectableCategoriesWhere(
    (schema, output) =>
      validateNodeConnection(
        schema.type,
        targetType,
        inputId,
        output.id,
        undefined,
        targetData as Record<string, unknown> | undefined,
      ).isValid,
  )
}
